import { ReactNode, useContext, useRef } from 'react';
import { sprintf } from 'sprintf-js';
import styled from 'styled-components';

import { Box } from 'components/Box';
import { Divider } from 'components/Divider';
import { PageToolbar } from 'components/Page/PageToolbar';
import { PrintHidden } from 'components/PrintHidden/PrintHidden';
import { BodySmall } from 'components/Text/BodySmall';
import { Layout } from 'layouts/Layout/Layout';
import {
  colors,
  cssBreakpoints,
  gutterWidth,
  maxWidth,
  transition,
} from 'theme/theme';
import { AmpContext } from 'utils/AmpContext';
import { LISTING_TYPE_HUMAN_NAME_MAP } from 'utils/constants/general/listingTypeHumanNameMap';

export const NAME_MAP = {
  ...LISTING_TYPE_HUMAN_NAME_MAP,
  IMPORTED: getText('opportunity'),
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const getMessage = ({ listingType, isOrg, isAdmin }: any) => {
  // @ts-expect-error TS(7053): Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
  const listingTypeName = NAME_MAP[listingType];
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line no-nested-ternary
  return isOrg
    ? sprintf(getText('Your organization posted this %(listingTypeName)s'), {
        listingTypeName,
      })
    : // TODO: Fix this the next time the file is edited.

    isAdmin
    ? sprintf(getText('You are an administrator of this %(listingTypeName)s'), {
        listingTypeName,
      })
    : sprintf(getText('You posted this %(listingTypeName)s'), {
        listingTypeName,
      });
};

const Body = styled.div`
  display: -ms-grid;
  display: grid;
  grid-template-columns: 100%;
  -ms-grid-columns: 100%;
  grid-template-rows: max-content;
  -ms-grid-rows: max-content;
  margin-left: auto;
  margin-right: auto;
  padding: 0 ${gutterWidth / 2}px;
  position: relative;
  max-width: ${maxWidth[3]}px;
  @media all and (min-width: ${cssBreakpoints.mdUp}) {
    padding: 0 ${gutterWidth}px;
    grid-template-columns: 288px calc(100% - 384px);
    -ms-grid-columns: 288px 96px calc(100% - 384px);
    grid-column-gap: 96px;
  }
  @media all and (min-width: ${cssBreakpoints.lgUp}) {
    grid-template-columns: 288px 768px;
    -ms-grid-columns: 288px 96px 768px;
    grid-column-gap: 96px;
  }
`;

const Header = styled(Box)`
  grid-row: 1;
  -ms-grid-row: 1;
  grid-column: 1;
  -ms-grid-column: 1;
  @media all and (min-width: ${cssBreakpoints.mdUp}) {
    grid-column: 2;
    -ms-grid-column: 3;
  }
`;

const Main = styled(Box)`
  grid-column: 1;
  -ms-grid-column: 1;
  grid-row: 3;
  -ms-grid-row: 3;
  @media all and (min-width: ${cssBreakpoints.mdUp}) {
    grid-column: 2;
    -ms-grid-column: 3;
    grid-row: 2;
    -ms-grid-row: 2;
  }
`;
const Sidebar = styled.div`
  background: white;
  border-radius: 4px;
  grid-row: 1 / 3;
  -ms-grid-row: 1;
  -ms-grid-row-span: 2;
  grid-column: 1;
  -ms-grid-column: 1;
  @media all and (max-width: ${cssBreakpoints.mdUp}) {
    grid-row: 2;
    -ms-grid-row: 2;
    -ms-grid-row-span: 1;
  }
`;
const SidebarContent = styled(Box)`
  @media all and (min-width: ${cssBreakpoints.mdUp}) {
    bottom: 40px;
    position: sticky;
    top: 150px;
  }
`;
const SidebarDetails = styled.div<{
  $isAmp?: boolean;
  $showSidebarDetails?: boolean;
}>`
  opacity: 1;
  transition:
    opacity ${transition},
    height 0.25s ease;

  @media all and (max-width: ${cssBreakpoints.mdUp}) {
    grid-row: 2;
    -ms-grid-row: 2;
    -ms-grid-row-span: 1;
  }
  ${(props) =>
    !props.$isAmp &&
    `
        @media all and (min-width: ${cssBreakpoints.mdUp}) {
          ${
            props.$showSidebarDetails
              ? `opacity: 1;`
              : `opacity: 0; pointer-events: none;

              > div {
                visibility: hidden;
              }
        `
          }
        }
      `};
`;

type Props = {
  adminActions?: ReactNode;
  toolbar?: ReactNode;
  isOrg?: boolean;
  isAdmin?: boolean;
  listingType?: 'JOB' | 'INTERNSHIP' | 'VOLOP' | 'ORG' | 'IMPORTED';
  sidebarTitle?: ReactNode | null | undefined;
  sidebarImage: ReactNode | null | undefined;
  sidebar: ReactNode;
  sidebarButtons?: ReactNode;
  sidebarLinks?: ReactNode;
  header: ReactNode;
  buttonBar?: ReactNode;
  main: ReactNode;
  banner?: ReactNode;
  footer?: ReactNode;
  navSection?: 'MAIN' | 'STAFF';
  urlByLocale: Record<string, string>;
};

export function ListingLayout({
  adminActions,
  toolbar,
  isOrg,
  isAdmin,
  listingType,
  sidebar,
  sidebarButtons,
  sidebarLinks,
  sidebarTitle,
  sidebarImage,
  header,
  buttonBar,
  main,
  banner,
  footer,
  navSection,
  urlByLocale,
}: Props) {
  const isAmp = useContext(AmpContext);

  const headerRef = useRef<HTMLDivElement>(null);

  return (
    <Layout
      buttonBar={buttonBar}
      defaultSearchType={listingType === 'IMPORTED' ? 'VOLOP' : listingType}
      navSection={navSection}
      urlByLocale={urlByLocale}
      defaultWithSearchHero={false}
    >
      {toolbar ||
        (adminActions && (
          <PageToolbar
            centered
            message={
              listingType && listingType !== 'IMPORTED'
                ? getMessage({
                    listingType,
                    isOrg,
                    isAdmin,
                  })
                : undefined
            }
            tools={adminActions}
          />
        ))}
      {banner}
      <Body>
        <Header
          // @ts-expect-error fix me
          ref={headerRef}
          mr={[0, '20px', '40px', '100px']}
          mt={[3, 3, 3, 4]}
          mb={[0, 0, 0, 3]}
        >
          {header}
        </Header>
        <Sidebar>
          <SidebarContent my={[0, 0, 0, 4]}>
            <BodySmall lineHeight="normal">
              {sidebarImage}
              <Box aria-hidden="true">{sidebarTitle}</Box>
              <Divider marginTop={24} marginBottom={24} />
              {sidebar}
              <SidebarDetails $showSidebarDetails $isAmp={isAmp}>
                <Box aria-hidden="true">{sidebarButtons}</Box>
                {sidebarLinks}
              </SidebarDetails>
            </BodySmall>
          </SidebarContent>
        </Sidebar>

        <Main mr={[0, '20px', '40px', '100px']}>{main}</Main>
      </Body>
      {footer && (
        <PrintHidden>
          <Box
            py={[gutterWidth, null, null, gutterWidth * 2]}
            bg={colors.backgroundGrey}
            mb={['-1px', null, null, -55]}
            textAlign="center"
          >
            <Box
              px={[gutterWidth / 2, null, null, gutterWidth]}
              maxWidth={maxWidth[3]}
              m="0 auto"
            >
              {footer}
            </Box>
          </Box>
        </PrintHidden>
      )}
    </Layout>
  );
}
